import { createContext, use, useCallback, useState } from 'react';
import styled from 'styled-components';
import { PermissionsApiPayload } from '../../../../../../common/src/api_types.ts';
import { HouseholdBase, Role, staffRoles, User } from '../../../../../../common/src/model.ts';
import * as server_api from '../../../../../../common/src/server_api.ts';
import { capitalize } from '../../../../../../common/src/util.ts';
import RightArrowIcon from '../../../../assets/arrow-next-right-icon.svg?react';
import { useCurrentOrg } from '../../../../data/use_current_org.tsx';
import { matches, SearchContext } from '../../../../util/search_control.tsx';
import { CheckMark } from '../../../../util/shared.tsx';
import { Menu, MenuEntry } from '../shared.tsx';
import { HouseholdTableContent, HouseholdTableScrollableWrapper } from './shared.tsx';

export const HouseholdFilterContext = createContext<{ filterOnStaff: boolean }>({} as any);

export function HouseholdsList({
  households,
  updateOrgInfo,
  minWidth,
}: {
  households: Array<HouseholdBase & { active?: boolean }>;
  updateOrgInfo?: () => Promise<void>;
  minWidth: string;
}) {
  const [expandedHouseholdToken, setExpandedHouseholdToken] = useState<string | undefined>();
  const { searchString } = use(SearchContext);
  const { filterOnStaff } = use(HouseholdFilterContext);

  const onClickHousehold = useCallback(
    (household: HouseholdBase) => {
      setExpandedHouseholdToken(household.token === expandedHouseholdToken ? undefined : household.token);
    },
    [expandedHouseholdToken],
  );

  return (
    <HouseholdTableScrollableWrapper $minWidth={minWidth}>
      <HouseholdTableContent>
        {households.length === 0 ? (
          <div>No {households.length === 0 ? 'households' : 'matches'}</div>
        ) : (
          households.map((household) => (
            <HouseholdTableRow
              key={household.token}
              household={household}
              onClickHousehold={onClickHousehold}
              isExpanded={
                !!updateOrgInfo &&
                (household.token === expandedHouseholdToken ||
                  filterOnStaff ||
                  !matches({ value: household.familyLastName, searchString }))
              }
              updateOrgInfo={updateOrgInfo}
            />
          ))
        )}
      </HouseholdTableContent>
    </HouseholdTableScrollableWrapper>
  );
}

function hasStaffRole(users: User[], orgId: number | undefined): boolean {
  return !!users.find(({ roles }) => roles?.find(({ role, orgId: id }) => id === orgId && staffRoles.includes(role)));
}

function HouseholdTableRow({
  household,
  onClickHousehold,
  isExpanded,
  updateOrgInfo,
}: {
  household: HouseholdBase;
  onClickHousehold: (household: HouseholdBase) => void;
  isExpanded: boolean;
  updateOrgInfo?: () => Promise<void>;
}) {
  const { searchString } = use(SearchContext);
  const { filterOnStaff } = use(HouseholdFilterContext);
  const { currentOrg } = useCurrentOrg();

  const onClick = useCallback(() => {
    if (!updateOrgInfo) {
      return;
    }
    onClickHousehold(household);
  }, [household, onClickHousehold, updateOrgInfo]);

  const { token, familyLastName, users, householdEmail } = household;

  return (
    <HouseholdTableRowWrapper>
      <HouseholdTableRowName
        key={token}
        onClick={onClick}
        $hasStaff={hasStaffRole(users, currentOrg?.id)}
        $isClickable={!!updateOrgInfo}
      >
        {updateOrgInfo && (isExpanded ? <DownArrow /> : <RightArrow />)}
        <FamilyLastName>{familyLastName}</FamilyLastName>
        {householdEmail && <HouseholdEmail>({householdEmail})</HouseholdEmail>}
      </HouseholdTableRowName>
      {isExpanded &&
        matchingUsers({ familyLastName, users, searchString, filterOnStaff, orgId: currentOrg?.id }).map((user) => (
          <HouseholdMember key={user.token} user={user} updateOrgInfo={updateOrgInfo} />
        ))}
    </HouseholdTableRowWrapper>
  );
}

const HouseholdTableRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const HouseholdTableRowName = styled.div<{ $hasStaff: boolean; $isClickable: boolean }>`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 5px;
  padding: 4px 0;
  ${(props) => (props.$isClickable ? 'cursor: pointer' : '')};
  ${(props) => (props.$hasStaff ? 'color: var(--color-dashboard-staff)' : '')}
`;

const checkMarkWidth = '20px';

function HouseholdMember({ user, updateOrgInfo }: { user: User; updateOrgInfo?: () => Promise<void> }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { currentOrg } = useCurrentOrg();

  const onClick = useCallback(() => {
    if (!updateOrgInfo) {
      return;
    }
    setMenuOpen(!menuOpen);
  }, [menuOpen, updateOrgInfo]);

  const onBlur = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <HouseholdMemberWrapper
      tabIndex={0}
      key={user.token}
      $isStaff={hasStaffRole([user], currentOrg?.id)}
      $isClickable={!!updateOrgInfo}
      onClick={onClick}
      onBlur={onBlur}
    >
      {user.name}
      {updateOrgInfo && menuOpen && (
        <RoleMenu>
          <RoleMenuEntry
            role={Role.Administrator}
            user={user}
            isFirst={true}
            isLast={false}
            updateOrgInfo={updateOrgInfo}
          />
          <RoleMenuEntry role={Role.Staff} user={user} isFirst={false} isLast={true} updateOrgInfo={updateOrgInfo} />
        </RoleMenu>
      )}
    </HouseholdMemberWrapper>
  );
}

const HouseholdMemberWrapper = styled.div<{ $isStaff: boolean; $isClickable: boolean }>`
  padding-left: 40px;
  ${(props) => (props.$isClickable ? 'cursor: pointer' : '')};
  ${(props) => (props.$isStaff ? 'color: var(--color-dashboard-staff)' : '')}
`;
const RoleMenu = styled(Menu)`
  color: var(--color-text);
`;

function RoleCheckMark({ isChecked }: { isChecked: boolean }) {
  return isChecked ? (
    <CheckMark $size={checkMarkWidth} $padding={'0'} $noAutoMargin={true} />
  ) : (
    <NoCheckMarkPlaceholder />
  );
}

const NoCheckMarkPlaceholder = styled.div`
  width: ${checkMarkWidth};
  flex-shrink: 0;
`;

function RoleMenuEntry({
  role,
  user,
  isFirst,
  isLast,
  updateOrgInfo,
}: {
  role: Role;
  user: User;
  isFirst: boolean;
  isLast: boolean;
  updateOrgInfo: () => Promise<void>;
}) {
  const { currentOrg } = useCurrentOrg();

  const isChecked = !!user.roles?.find(
    ({ role: currentRole, orgId }) => orgId === currentOrg?.id && currentRole === role,
  );

  const onClick = useCallback(() => {
    // the org admin's Bearer token (attached by server_api) authorizes the change
    const payload: PermissionsApiPayload = {
      userToken: user.token,
      role,
      orgId: currentOrg?.id,
    };
    if (isChecked) {
      void (async () => {
        await server_api.revokePermissions(payload);
        await updateOrgInfo();
      })();
    } else {
      void (async () => {
        await server_api.setPermissions(payload);
        await updateOrgInfo();
      })();
    }
  }, [currentOrg?.id, isChecked, role, updateOrgInfo, user.token]);

  return (
    <MenuEntry $isFirst={isFirst} $isLast={isLast} onClick={onClick}>
      <RoleCheckMark isChecked={isChecked} />
      {capitalize(role)}
    </MenuEntry>
  );
}

function matchingUsers({
  familyLastName,
  users,
  searchString,
  filterOnStaff,
  orgId,
}: {
  familyLastName: string;
  users: User[];
  searchString: string;
  filterOnStaff: boolean;
  orgId: number | undefined;
}): User[] {
  return users.filter(
    ({ name, roles }) =>
      matches({ value: `${name} ${familyLastName}`, searchString }) &&
      (!filterOnStaff || roles?.find(({ role, orgId: id }) => id === orgId && role === Role.Staff)),
  );
}

export const FamilyLastName = styled.div``;

const HouseholdEmail = styled.div`
  color: var(--color-text-lighter);
  margin-left: 1ch;
  text-wrap: nowrap;
`;

export const RightArrow = styled(RightArrowIcon)`
  height: 15px;
  width: 15px;
  flex-shrink: 0;
  fill: var(--color-text);
`;

export const DownArrow = styled(RightArrowIcon)`
  height: 15px;
  width: 15px;
  transform: rotate(90deg);
  flex-shrink: 0;
  fill: var(--color-text);
`;
